import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useChallengeDetail } from '../hooks/useChallengeDetail'
import { useProofsByRound, type ProofInfo } from '../hooks/useGallery'
import { useReport, parseReportError } from '../hooks/useReport'
import { useWallet } from '../contexts/WalletContext'
import { formatAddress, formatTimestamp, formatTime, type ProofType } from '../types/challenge'

const REASON_OPTIONS = ['목표와 무관한 사진', '중복 / 재활용 사진', '날짜·시각 조작 의심', '기타']

function getRoundLabel(roundDeadlines: number[], index: number): string {
  if (roundDeadlines.length < 2) return `${index + 1}라운드`
  const spacing = roundDeadlines[1] - roundDeadlines[0]
  return spacing >= 500_000 ? `${index + 1}주차` : `${index + 1}일차`
}

function getImageUrl(cidHash: string): string {
  const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? ''
  return `${apiBase}/api/images/${cidHash}`
}

function proofBadgeClass(proof: ProofInfo) {
  if (proof.isEliminated) return 'badge badge-neutral'
  if (proof.proofStatus === 'valid') return 'badge badge-success'
  if (proof.proofStatus === 'review') return 'badge badge-warning'
  return 'badge badge-danger'
}
function proofBadgeLabel(proof: ProofInfo) {
  if (proof.isEliminated) return '탈락'
  if (proof.proofStatus === 'valid') return '유효 ✓'
  if (proof.proofStatus === 'review') return '검토 중'
  return '무효'
}

function ProofCard({
  proof,
  proofType,
  userAddress,
  onReport,
}: {
  proof: ProofInfo
  proofType: ProofType
  userAddress: string | null
  onReport: (p: ProofInfo) => void
}) {
  const [imgError, setImgError] = useState(false)
  const [text, setText] = useState<string | null>(null)
  const isOwn = userAddress?.toLowerCase() === proof.participant
  const canReport = !isOwn && !proof.isEliminated

  useEffect(() => {
    if (proofType !== 'text') return
    let active = true
    const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? ''
    void fetch(`${apiBase}/api/proof/${proof.cidHash}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (active && d) setText((d as { text?: string }).text ?? null)
      })
      .catch(() => {})
    return () => {
      active = false
    }
  }, [proofType, proof.cidHash])

  return (
    <div
      className={`pcard${proof.isEliminated ? ' elim' : ''}`}
      data-testid={`proof-card-${proof.participant}`}
    >
      <div className="pimg">
        {proofType === 'photo' ? (
          imgError ? (
            <div
              style={{
                display: 'grid',
                placeItems: 'center',
                height: '100%',
                fontSize: 28,
                color: 'var(--border-2)',
              }}
            >
              📷
            </div>
          ) : (
            <img
              src={getImageUrl(proof.cidHash)}
              alt={`${proof.participant} 인증샷`}
              onError={() => setImgError(true)}
            />
          )
        ) : proofType === 'text' ? (
          <div
            style={{
              padding: 12,
              height: '100%',
              overflow: 'auto',
              fontSize: 13,
              lineHeight: 1.5,
              color: 'var(--ink-2)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {text ?? '…'}
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              placeItems: 'center',
              alignContent: 'center',
              gap: 4,
              height: '100%',
              color: 'var(--success)',
            }}
          >
            <div style={{ fontSize: 30 }}>✅</div>
            {proof.submittedAt > 0 && (
              <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'var(--font-mono)' }}>
                {formatTime(proof.submittedAt)}
              </div>
            )}
            <div style={{ fontSize: 12, fontWeight: 600 }}>체크인 완료</div>
          </div>
        )}
      </div>
      <div className="pcard-body">
        <div className="pcard-head">
          <span className="mono" style={{ fontSize: 12, fontWeight: 600 }}>
            {formatAddress(proof.participant)}
          </span>
          <span
            className={proofBadgeClass(proof)}
            data-testid={`proof-status-${proof.participant}`}
          >
            {proofBadgeLabel(proof)}
          </span>
        </div>
        {proof.submittedAt > 0 && (
          <span style={{ fontSize: 11, color: 'var(--ink-4)' }}>
            {formatTimestamp(proof.submittedAt)} {formatTime(proof.submittedAt)}
          </span>
        )}
        {canReport && (
          <button
            className="btn btn-ghost btn-sm"
            style={{ marginTop: 4 }}
            onClick={() => onReport(proof)}
            data-testid={`report-btn-${proof.participant}`}
          >
            신고
          </button>
        )}
      </div>
    </div>
  )
}

function ReportModal({
  proof,
  challengeAddress,
  onClose,
  onSuccess,
}: {
  proof: ProofInfo
  challengeAddress: string
  onClose: () => void
  onSuccess: () => void
}) {
  const [selectedReason, setSelectedReason] = useState<number | null>(null)
  const [reasonDetail, setReasonDetail] = useState('')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const { mutate: report, isPending } = useReport(challengeAddress)

  function handleSubmit() {
    if (selectedReason === null) {
      setErrorMsg('신고 사유를 선택해주세요')
      return
    }
    const isOther = selectedReason === REASON_OPTIONS.length - 1
    if (isOther && !reasonDetail.trim()) {
      setErrorMsg('기타 사유를 입력해주세요')
      return
    }
    setErrorMsg(null)
    report(
      {
        targetAddress: proof.participant,
        round: proof.round,
        reason: REASON_OPTIONS[selectedReason],
        reasonDetail: isOther ? reasonDetail.trim() : '',
      },
      {
        onSuccess: () => {
          onSuccess()
          onClose()
        },
        onError: (err) => setErrorMsg(parseReportError(err)),
      },
    )
  }

  return (
    <div className="modal-scrim" data-testid="report-modal">
      <div className="modal">
        <div className="modal-head">
          <h2 className="modal-title">인증 신고</h2>
          <button className="btn btn-quiet btn-sm" onClick={onClose} aria-label="닫기">
            ✕
          </button>
        </div>
        <div className="modal-body">
          <img
            src={getImageUrl(proof.cidHash)}
            alt="신고 대상"
            style={{
              width: '100%',
              height: 150,
              objectFit: 'cover',
              borderRadius: 'var(--radius)',
              background: 'var(--surface-3)',
            }}
            onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = 'none')}
          />
          <div className="field">
            <label className="field-label">신고 사유</label>
            {REASON_OPTIONS.map((opt, i) => (
              <label
                key={opt}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 8,
                  cursor: 'pointer',
                  fontSize: 14,
                }}
              >
                <input
                  type="radio"
                  name="report-reason"
                  checked={selectedReason === i}
                  onChange={() => setSelectedReason(i)}
                  data-testid={`reason-opt-${i}`}
                />{' '}
                {opt}
              </label>
            ))}
            {selectedReason === REASON_OPTIONS.length - 1 && (
              <textarea
                className="textarea"
                style={{ minHeight: 60 }}
                placeholder="기타 사유를 입력해주세요..."
                value={reasonDetail}
                onChange={(e) => setReasonDetail(e.target.value)}
                data-testid="reason-detail"
              />
            )}
          </div>
          {errorMsg && (
            <div className="alert alert-danger" role="alert">
              {errorMsg}
            </div>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose} data-testid="btn-cancel-report">
            취소
          </button>
          <button
            className="btn btn-danger"
            onClick={handleSubmit}
            disabled={isPending}
            data-testid="btn-submit-report"
          >
            {isPending ? '제출 중…' : '신고 제출'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function Gallery() {
  const { address } = useParams<{ address: string }>()
  const { address: userAddress } = useWallet()
  const [selectedRound, setSelectedRound] = useState(0)
  const [reportTarget, setReportTarget] = useState<ProofInfo | null>(null)
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  const { data: challenge, isLoading: challengeLoading } = useChallengeDetail(address ?? '', null)
  const proofType: ProofType = challenge?.proofType ?? 'photo'
  const { data: proofs = [], isLoading: proofsLoading } = useProofsByRound(
    address ?? '',
    selectedRound,
  )
  const roundDeadlines = challenge?.roundDeadlines ?? []

  function showToast(type: 'success' | 'error', msg: string) {
    setToast({ type, message: msg })
    setTimeout(() => setToast(null), 4000)
  }

  if (!address)
    return (
      <div className="empty">
        <p>잘못된 챌린지 주소입니다.</p>
      </div>
    )

  const activeProofs = proofs.filter((p) => !p.isEliminated)
  const eliminatedProofs = proofs.filter((p) => p.isEliminated)

  return (
    <div>
      <Link
        to={`/challenge/${address}`}
        className="btn btn-quiet btn-sm"
        style={{ marginBottom: 16, paddingLeft: 0 }}
      >
        ← 챌린지로 돌아가기
      </Link>
      <div className="pg-head">
        <h1 className="pg-title">{challenge?.name ?? address} — 인증 갤러리</h1>
      </div>

      {/* Round tabs */}
      {roundDeadlines.length > 0 && (
        <div className="tabs" style={{ marginBottom: 24 }}>
          {roundDeadlines.map((_, i) => (
            <button
              key={i}
              className={`tab-btn${selectedRound === i ? ' active' : ''}`}
              onClick={() => setSelectedRound(i)}
              data-testid={`round-tab-${i}`}
            >
              {getRoundLabel(roundDeadlines, i)}
            </button>
          ))}
        </div>
      )}

      {(challengeLoading || proofsLoading) && (
        <div className="empty">
          <div className="spinner spinner-lg" />
          <p>불러오는 중…</p>
        </div>
      )}

      {!challengeLoading && !proofsLoading && (
        <>
          {activeProofs.length > 0 ? (
            <div className="pgrid">
              {activeProofs.map((p) => (
                <ProofCard
                  key={p.participant}
                  proof={p}
                  proofType={proofType}
                  userAddress={userAddress}
                  onReport={setReportTarget}
                />
              ))}
            </div>
          ) : (
            !eliminatedProofs.length && (
              <div className="empty">
                <p>이 라운드에 제출된 인증이 없습니다.</p>
              </div>
            )
          )}
          {eliminatedProofs.length > 0 && (
            <>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--ink-4)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  margin: '24px 0 12px',
                }}
              >
                탈락자
              </div>
              <div className="pgrid">
                {eliminatedProofs.map((p) => (
                  <ProofCard
                    key={p.participant}
                    proof={p}
                    proofType={proofType}
                    userAddress={userAddress}
                    onReport={setReportTarget}
                  />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {reportTarget && address && (
        <ReportModal
          proof={reportTarget}
          challengeAddress={address}
          onClose={() => setReportTarget(null)}
          onSuccess={() => showToast('success', '신고가 접수되었습니다.')}
        />
      )}

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 2000 }}>
          <div
            className={`alert ${toast.type === 'success' ? 'alert-success' : 'alert-danger'}`}
            style={{ boxShadow: 'var(--shadow-md)' }}
          >
            {toast.message}
          </div>
        </div>
      )}
    </div>
  )
}
